import { ApiResponse, JwtDecodeData } from './api.types';
import jwtDecode from 'jwt-decode';
import { Moment } from 'moment';
import download from 'downloadjs';

export default class ApiService {
    public static instance: ApiService;

    private API_BASE_URL: string;
    private PORT: number;

    private readonly JWT_TOKEN_KEY = 'auth_token';
    private readonly USERNAME_KEY = 'username';

    constructor() {
        this.API_BASE_URL = '';
        this.PORT = 0;
    }

    static getInstance(): ApiService {
        if (!ApiService.instance) {
            ApiService.instance = new ApiService();
        }

        return ApiService.instance;
    }

    setBaseUrl(apiBaseUrl: string) {
        this.API_BASE_URL = apiBaseUrl;
    }

    setPort(port: number) {
        this.PORT = port;
    }

    /**
     * Login user, /api/login
     * Loggin spectator here will return error response
     * @param username
     * @param password
     * @returns - response data
     */
    async login(username: string, password: string) {
        const data = new URLSearchParams();
        data.append('username', username);
        data.append('password', password);

        try {
            const response = await fetch(`${this.API_BASE_URL}:${this.PORT}/api/login`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
                },
                body: data,
            });

            if (response.ok) {
                const data = await response.json();

                if (typeof data.username === 'undefined') {
                    return this.constructUnknownErrorResponse();
                }

                this.storeUsername(data.username);
                return this.constructorSucessResponse(null);
            } else {
                if (response.status === 401) {
                    return this.constructValidErrorResponse('Invalid username or password');
                } else {
                    return this.constructUnknownErrorResponse();
                }
            }
        } catch (e) {
            return this.constructUnknownErrorResponse();
        }
    }

    async getDevicesInfo() {
        try {
            const response = await fetch(`${this.API_BASE_URL}:${this.PORT}/api/devices/get_all`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
                },
            });

            if (response.ok) {
                const data = await response.json();

                return this.constructorSucessResponse(data.devices);
            } else {
                if (response.status === 401 || response.status === 403) {
                    this.logout();
                } else {
                    return this.constructUnknownErrorResponse();
                }
            }
        } catch (e) {
            return this.constructUnknownErrorResponse();
        }
    }

    async getChart1Data(deviceId: number, datetime: Moment) {
        const data = new URLSearchParams();
        data.append('deviceId', JSON.stringify(deviceId));
        data.append('datetime', JSON.stringify(datetime.unix()));

        try {
            const response = await fetch(`${this.API_BASE_URL}:${this.PORT}/api/data/chart1`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
                },
                body: data,
            });

            if (response.ok) {
                const data = await response.json();
                return this.constructorSucessResponse(data.data);
            } else {
                if (response.status === 401 || response.status === 403) {
                    this.logout();
                } else {
                    return this.constructUnknownErrorResponse();
                }
            }
        } catch (e) {
            return this.constructUnknownErrorResponse();
        }
    }

    async getChart1CarsData(deviceId: number, datetime: Moment) {
        const data = new URLSearchParams();
        data.append('deviceId', JSON.stringify(deviceId));
        data.append('datetime', JSON.stringify(datetime.unix()));

        try {
            const response = await fetch(`${this.API_BASE_URL}:${this.PORT}/api/data/chart1_cars`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
                },
                body: data,
            });

            if (response.ok) {
                const data = await response.json();
                return this.constructorSucessResponse(data.data);
            } else {
                if (response.status === 401 || response.status === 403) {
                    this.logout();
                } else {
                    return this.constructUnknownErrorResponse();
                }
            }
        } catch (e) {
            return this.constructUnknownErrorResponse();
        }
    }

    async getPieChartData(deviceId: number, requestedDay: string) {
        const data = new URLSearchParams();
        data.append('deviceId', JSON.stringify(deviceId));
        data.append('requestedDay', JSON.stringify(requestedDay));

        try {
            const response = await fetch(`${this.API_BASE_URL}:${this.PORT}/api/data/pie_chart`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
                },
                body: data,
            });

            if (response.ok) {
                const data = await response.json();
                return this.constructorSucessResponse(data.data);
            } else {
                if (response.status === 401 || response.status === 403) {
                    this.logout();
                } else {
                    return this.constructUnknownErrorResponse();
                }
            }
        } catch (e) {
            return this.constructUnknownErrorResponse();
        }
    }

    async getPieChartDataCars(deviceId: number, requestedDay: string) {
        const data = new URLSearchParams();
        data.append('deviceId', JSON.stringify(deviceId));
        data.append('requestedDay', JSON.stringify(requestedDay));

        try {
            const response = await fetch(`${this.API_BASE_URL}:${this.PORT}/api/data/pie_chart_cars`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
                },
                body: data,
            });

            if (response.ok) {
                const data = await response.json();
                return this.constructorSucessResponse(data.data);
            } else {
                if (response.status === 401 || response.status === 403) {
                    this.logout();
                } else {
                    return this.constructUnknownErrorResponse();
                }
            }
        } catch (e) {
            return this.constructUnknownErrorResponse();
        }
    }

    async getChart2Data(
        deviceId: number,
        datetimeFrom: Moment,
        datetimeTo: Moment,
        numOfSegments: number,
        type: string,
    ) {
        const data = new URLSearchParams();
        data.append('deviceId', JSON.stringify(deviceId));
        data.append('timeFrom', JSON.stringify(datetimeFrom.unix()));
        data.append('timeTo', JSON.stringify(datetimeTo.unix()));
        data.append('numOfSegments', JSON.stringify(numOfSegments));
        data.append('type', type);

        try {
            const response = await fetch(`${this.API_BASE_URL}:${this.PORT}/api/data/chart2`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
                },
                body: data,
            });

            if (response.ok) {
                const data = await response.json();
                return this.constructorSucessResponse(data.data);
            } else {
                if (response.status === 401 || response.status === 403) {
                    this.logout();
                } else {
                    return this.constructUnknownErrorResponse();
                }
            }
        } catch (e) {
            return this.constructUnknownErrorResponse();
        }
    }

    async getChart2CarsData(
        deviceId: number,
        datetimeFrom: Moment,
        datetimeTo: Moment,
        numOfSegments: number,
        type: string,
    ) {
        const data = new URLSearchParams();
        data.append('deviceId', JSON.stringify(deviceId));
        data.append('timeFrom', JSON.stringify(datetimeFrom.unix()));
        data.append('timeTo', JSON.stringify(datetimeTo.unix()));
        data.append('numOfSegments', JSON.stringify(numOfSegments));
        data.append('type', type);

        try {
            const response = await fetch(`${this.API_BASE_URL}:${this.PORT}/api/data/chart2_cars`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
                },
                body: data,
            });

            if (response.ok) {
                const data = await response.json();
                return this.constructorSucessResponse(data.data);
            } else {
                if (response.status === 401 || response.status === 403) {
                    this.logout();
                } else {
                    return this.constructUnknownErrorResponse();
                }
            }
        } catch (e) {
            return this.constructUnknownErrorResponse();
        }
    }

    async getDeviceDailyData(deviceId: number, requestedDay: string) {
        const data = new URLSearchParams();
        data.append('deviceId', JSON.stringify(deviceId));
        data.append('requestedDay', JSON.stringify(requestedDay));

        try {
            const response = await fetch(`${this.API_BASE_URL}:${this.PORT}/api/data/day_data`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
                },
                body: data,
            });

            if (response.ok) {
                const data = await response.json();
                return this.constructorSucessResponse(data.data);
            } else {
                if (response.status === 401 || response.status === 403) {
                    this.logout();
                } else {
                    return this.constructUnknownErrorResponse();
                }
            }
        } catch (e) {
            return this.constructUnknownErrorResponse();
        }
    }

    async getDeviceDailyCarsData(deviceId: number, requestedDay: string) {
        const data = new URLSearchParams();
        data.append('deviceId', JSON.stringify(deviceId));
        data.append('requestedDay', JSON.stringify(requestedDay));

        try {
            const response = await fetch(`${this.API_BASE_URL}:${this.PORT}/api/data/day_data_cars`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
                },
                body: data,
            });

            if (response.ok) {
                const data = await response.json();
                return this.constructorSucessResponse(data.data);
            } else {
                if (response.status === 401 || response.status === 403) {
                    this.logout();
                } else {
                    return this.constructUnknownErrorResponse();
                }
            }
        } catch (e) {
            return this.constructUnknownErrorResponse();
        }
    }

    async getInitialQueryData(
        devices: number[],
        datetimeFrom: Moment,
        datetimeTo: Moment,
        pageSize: number,
        orderBy: object,
    ) {
        const data = new URLSearchParams();
        data.append('devices', JSON.stringify(devices));
        data.append('timeFrom', JSON.stringify(datetimeFrom.unix()));
        data.append('timeTo', JSON.stringify(datetimeTo.unix()));
        data.append('pageSize', JSON.stringify(pageSize));
        data.append('orderBy', JSON.stringify(orderBy));

        try {
            const response = await fetch(`${this.API_BASE_URL}:${this.PORT}/api/query_data/preview`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
                },
                body: data,
            });

            if (response.ok) {
                const data = await response.json();
                return this.constructorSucessResponse(data.data);
            } else {
                if (response.status === 401 || response.status === 403) {
                    this.logout();
                } else {
                    return this.constructUnknownErrorResponse();
                }
            }
        } catch (e) {
            return this.constructUnknownErrorResponse();
        }
    }

    async getPageQueryData(
        devices: number[],
        datetimeFrom: Moment,
        datetimeTo: Moment,
        page: number,
        pageSize: number,
        orderBy: object,
    ) {
        const data = new URLSearchParams();
        data.append('devices', JSON.stringify(devices));
        data.append('timeFrom', JSON.stringify(datetimeFrom.unix()));
        data.append('timeTo', JSON.stringify(datetimeTo.unix()));
        data.append('page', JSON.stringify(page));
        data.append('pageSize', JSON.stringify(pageSize));
        data.append('orderBy', JSON.stringify(orderBy));

        try {
            const response = await fetch(`${this.API_BASE_URL}:${this.PORT}/api/query_data/preview`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
                },
                body: data,
            });

            if (response.ok) {
                const data = await response.json();
                return this.constructorSucessResponse(data.data);
            } else {
                if (response.status === 401 || response.status === 403) {
                    this.logout();
                } else {
                    return this.constructUnknownErrorResponse();
                }
            }
        } catch (e) {
            return this.constructUnknownErrorResponse();
        }
    }

    async getQueryExport(
        devices: number[],
        datetimeFrom: Moment,
        datetimeTo: Moment,
        orderBy: object,
        format: string,
        interval: string,
    ) {
        const data = new URLSearchParams();
        data.append('devices', JSON.stringify(devices));
        data.append('timeFrom', JSON.stringify(datetimeFrom.unix()));
        data.append('timeTo', JSON.stringify(datetimeTo.unix()));
        data.append('orderBy', JSON.stringify(orderBy));
        data.append('format', format);
        data.append('interval', interval);

        let acceptApplication;
        switch (format) {
            case 'csv':
                acceptApplication = 'application/csv';
                break;
            case 'json':
                acceptApplication = 'application/json';
                break;
            case 'xml':
                acceptApplication = 'application/xml';
                break;
            default:
                acceptApplication = 'application/json';
        }

        try {
            const response = await fetch(`${this.API_BASE_URL}:${this.PORT}/api/query_data/export`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    Accept: acceptApplication,
                    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
                },
                body: data,
            });

            if (response.ok) {
                const blobResponse = await response.blob();
                download(blobResponse, `export.${format}`);

                return;
            } else {
                if (response.status === 401 || response.status === 403) {
                    this.logout();
                } else {
                    return this.constructUnknownErrorResponse();
                }
            }
        } catch (e) {
            return this.constructUnknownErrorResponse();
        }
    }

    async refreshToken() {
        try {
            const response = await fetch(`${this.API_BASE_URL}:${this.PORT}/api/refresh_token`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
                },
            });

            if (!response.ok) {
                this.logout();
            }
        } catch (e) {
            return this.constructUnknownErrorResponse();
        }
    }

    storeUsername(username: string) {
        localStorage.setItem(this.USERNAME_KEY, username);
    }

    async logout() {
        console.log('Logging out user');
        this.removeUsernameData();

        await fetch(`${this.API_BASE_URL}:${this.PORT}/api/logout`, {
            method: 'POST',
            credentials: 'include',
        });

        window.location.href = '/';
    }

    hasJWTToken() {
        return localStorage.getItem(this.JWT_TOKEN_KEY) !== null;
    }

    hasUsername() {
        return localStorage.getItem(this.USERNAME_KEY) !== null;
    }

    getUsername() {
        const username = localStorage.getItem(this.USERNAME_KEY);
        if (username === null) {
            this.logout();
        } else {
            return username;
        }
    }

    removeUsernameData() {
        localStorage.removeItem(this.USERNAME_KEY);
    }

    constructorSucessResponse(data: any): ApiResponse {
        return {
            success: true,
            data: data,
        };
    }

    constructValidErrorResponse(errorMessage: string): ApiResponse {
        return {
            success: false,
            validError: true,
            errorMessage: errorMessage,
        };
    }

    constructUnknownErrorResponse(): ApiResponse {
        return {
            success: false,
            validError: false,
            errorMessage: 'Error while communicating with server',
        };
    }
}
